const { get, mapValues, last, sum, sumBy, groupBy, keyBy, sortBy, omit, isEmpty, fromPairs, } = require('lodash');

const { accountItemCategories } = require('../config');
const { fieldDisplayValue, integerFormat, integerChangeFormat, } = require('../util');
const { cfMappingItemFields, } = require('../models/consolidationAccountItem');
const { generalSegment, } = require('../models/consolidationSegment');
const { noneSection, } = require('../models/section');

const { keys } = Object;
const accountItemCategoriesByName = keyBy(accountItemCategories, 'name');
const accountItemCategoriesGroupedByType = groupBy(accountItemCategories, 'type');

const generateRowGroups = (items, consolidationCalculationItems, allConsolidationSegments, showsAll) => {
  const categoryItems = items.filter(_ => _.itemType === 'category');
  const itemsGroupedByCategory = groupBy(items, 'accountItemCategory.name');
  const categoryItemsByCategory = keyBy(categoryItems, 'accountItemCategory.name');
  const consolidationCalculationItemsByKey = keyBy(consolidationCalculationItems, 'key');
  return ['bs', 'pl', 'cr'].map((type) => {
    const accountItemCategories = accountItemCategoriesGroupedByType[type];
    return {
      type,
      rowGroups: accountItemCategories.map((category) => {
        const sign = ({ debit: 1, credit: -1 })[category.direction];
        const directItems = (itemsGroupedByCategory[category.name] || []).filter(_ => _.itemType === 'consolidationAccountItem');
        const categoryItem = categoryItemsByCategory[category.name];
        const rowGroups = [...directItems, categoryItem].map((item) => {
          const { companyItems, segmentColumns, key, } = item;
          const consolidationCalculationItem = consolidationCalculationItemsByKey[key];
          const companyRows = companyItems.map(({ company, segmentColumns }) => {
            return {
              rowType: 'company',
              metricLabel: '個別',
              companyName: company.display_name,
              segmentColumns: segmentColumns.map(_ => ({ ..._, amount: _.exchangedClosingBalance })),
            };
          });
          const individualAdjustmentJournalRow = {
            rowType: 'individualAdjustmentJournal',
            metricLabel: '個別修正',
            segmentColumns: allConsolidationSegments.map((segment, i) => {
              return {
                segment,
                amount: sumBy(companyItems, ({ segmentColumns }) => {
                  const segmentColumn = segmentColumns[i];
                  return segmentColumn.exchangedAdjustmentAmount;
                }),
              };
            })
          };
          const consolidationJournalRow = {
            rowType: 'consolidationJournal',
            metricLabel: '連結仕訳',
            segmentColumns: segmentColumns.map(({ segment, consolidationAmount, }) => {
              return {
                segment,
                amount: consolidationAmount,
              };
            })
          };
          const consolidatedRow = {
            rowType: 'consolidated',
            metricLabel: '連結消去後',
            segmentColumns: allConsolidationSegments.map((segment, i) => {
              return {
                segment,
                amount: sumBy([...companyRows, individualAdjustmentJournalRow, consolidationJournalRow], _ => _.segmentColumns[i].amount),
              };
            })
          };
          const intersegmentRow = {
            rowType: 'intersegment',
            metricLabel: 'セグメント間',
            segmentColumns: segmentColumns.map(({ segment, consolidationIntersegmentAmount, }) => {
              const amount = segment.id === 'general' ? sumBy(segmentColumns, 'consolidationIntersegmentAmount') : consolidationIntersegmentAmount * -1;
              return {
                segment,
                amount,
              };
            })
          };
          const totalRow = {
            rowType: 'total',
            metricLabel: '合計',
            segmentColumns: allConsolidationSegments.map((segment, i) => {
              return {
                segment,
                amount: sumBy([...companyRows, individualAdjustmentJournalRow, consolidationJournalRow, intersegmentRow], _ => _.segmentColumns[i].amount),
              };
            }),
          };
          return {
            item,
            rows: [
              ...companyRows,
              individualAdjustmentJournalRow,
              consolidationJournalRow,
              consolidatedRow,
              intersegmentRow,
              totalRow,
            ].map((row) => {
              const { rowType, segmentColumns } = row;
              const segmentWithoutGeneralColumns = segmentColumns.filter(_ => _.segment.id !== 'general').map(_ => ({ ..._, columnType: 'segment' }));
              const totalWithoutGeneralColumn = {
                columnType: 'totalWithoutGeneral',
                amount: sumBy(segmentWithoutGeneralColumns, 'amount'),
              };
              const generalColumn = {
                columnType: 'general',
                ...segmentColumns.find(_ => _.segment.id === 'general'),
              };
              const totalColumn = {
                columnType: 'total',
                amount: sumBy(segmentColumns, 'amount'),
              };
              const consolidationCalculationColumn = {
                columnType: 'consolidationCalculation',
                amount: rowType === 'total' ? consolidationCalculationItem?.conclusiveAmount : null,
              };
              const diffColumn = {
                columnType: 'diff',
                format: integerChangeFormat,
                amount: rowType === 'total' ? (sumBy(segmentColumns, 'amount') - consolidationCalculationItem?.conclusiveAmount || 0) : null,
              };
              return {
                ...row,
                columns: [
                  ...segmentWithoutGeneralColumns,
                  totalWithoutGeneralColumn,
                  generalColumn,
                  totalColumn,
                  consolidationCalculationColumn,
                  diffColumn,
                ].map((column, i) => {
                  const { columnType, } = column;
                  const label = columnType === 'segment' ? allConsolidationSegments[i]?.name : columnType;
                  return {
                    ...column,
                    label,
                  };
                }),
              };
            }),
          };
        }).filter(_ => showsAll || last(_.rows).columns.some(_ => Math.abs(_.amount) > 0));
        return {
          category,
          rowGroups,
        };
      }),
    };
  });
};

const monthlyTrendsRowTypes = ['individual', 'individualAdjustmentJournal', 'consolidationJournal', 'consolidated', 'intersegment', 'total'];

const rowTypeLabels = {
  individual: '個別',
  individualAdjustmentJournal: '個別修正',
  consolidationJournal: '連結仕訳',
  consolidated: '連結消去後',
  intersegment: 'セグメント間',
  total: '合計',
};

const segmentColumnsOfRows = (rows) => {
  return {
    companyRowsGroupedByCompanyName: groupBy(rows.filter(_ => _.rowType === 'company'), 'companyName'),
    rowsByRowType: keyBy(rows, 'rowType'),
  };
};

// NOTE: 各月の金額は期首からの累計で算出されているため、発生額を出すには前月との差分を取る
const issuedMonthColumns = (monthColumns) => {
  return monthColumns.map((monthColumn, i) => {
    const prevAmount = monthColumns[i - 1]?.amount;
    return {
      ...monthColumn,
      amount: monthColumn.amount == null ? null : monthColumn.amount - (prevAmount || 0),
    };
  });
};

const monthColumnsOfType = (type, monthColumns) => type === 'bs' ? monthColumns : issuedMonthColumns(monthColumns);

const totalMonthColumnOf = (type, monthColumns) => {
  const amounts = monthColumns.map(_ => _.amount).filter(_ => _ != null);
  if(type === 'bs' || isEmpty(amounts)) return null;
  return { amount: sum(amounts) };
};

const generateMonthlyTrendsRowGroups = (monthItems, allConsolidationSegments, showsAll) => {
  const monthRowGroups = monthItems.map(({ yearMonth, items, consolidationCalculationItems, }) => {
    return {
      yearMonth,
      rowGroupsByKey: keyBy(
        generateRowGroups(items, consolidationCalculationItems, allConsolidationSegments, true).flatMap(_ => _.rowGroups).flatMap(_ => _.rowGroups),
        _ => _.item.key,
      ),
    };
  });
  const [firstMonth] = monthRowGroups;
  if(!firstMonth) return [];

  const segmentsWithGeneral = [...allConsolidationSegments.filter(_ => _.id !== 'general'), ...allConsolidationSegments.filter(_ => _.id === 'general')];

  return ['bs', 'pl', 'cr'].map((type) => {
    const categories = accountItemCategoriesGroupedByType[type];
    return {
      type,
      rowGroups: categories.map((category) => {
        const itemKeysOfCategory = keys(firstMonth.rowGroupsByKey).filter((key) => {
          return firstMonth.rowGroupsByKey[key].item.accountItemCategory?.name === category.name;
        });
        const orderedItemKeys = sortBy(itemKeysOfCategory, key => firstMonth.rowGroupsByKey[key].item.itemType === 'category' ? 1 : 0);
        const itemRowGroups = orderedItemKeys.map((key) => {
          const { item } = firstMonth.rowGroupsByKey[key];
          const monthRowsByYearMonth = monthRowGroups.map(({ yearMonth, rowGroupsByKey }) => {
            const rows = rowGroupsByKey[key]?.rows || [];
            return { yearMonth, ...segmentColumnsOfRows(rows) };
          });
          const companyNames = (firstMonth.rowGroupsByKey[key]?.rows || []).filter(_ => _.rowType === 'company').map(_ => _.companyName);
          const segmentRowGroups = segmentsWithGeneral.map((segment) => {
            const segmentIndex = allConsolidationSegments.findIndex(_ => _.id === segment.id);
            const amountOf = (row) => row?.segmentColumns?.[segmentIndex]?.amount ?? null;
            const companyMetricRows = companyNames.map((companyName) => {
              const monthColumns = monthColumnsOfType(type, monthRowsByYearMonth.map(({ yearMonth, companyRowsGroupedByCompanyName }) => {
                const row = companyRowsGroupedByCompanyName[companyName]?.[0];
                return { yearMonth, amount: amountOf(row), segment, };
              }));
              return {
                rowType: 'company',
                metricLabel: rowTypeLabels.individual,
                companyName,
                monthColumns,
                totalMonthColumn: totalMonthColumnOf(type, monthColumns),
              };
            });
            const metricRows = monthlyTrendsRowTypes.filter(_ => _ !== 'individual').map((rowType) => {
              const monthColumns = monthColumnsOfType(type, monthRowsByYearMonth.map(({ yearMonth, rowsByRowType }) => {
                return { yearMonth, amount: amountOf(rowsByRowType[rowType]), segment, };
              }));
              return {
                rowType,
                metricLabel: rowTypeLabels[rowType],
                monthColumns,
                totalMonthColumn: totalMonthColumnOf(type, monthColumns),
              };
            });
            return {
              segment,
              metricRows: [...companyMetricRows, ...metricRows],
            };
          });
          const totalMonthColumns = monthColumnsOfType(type, monthRowsByYearMonth.map(({ yearMonth, rowsByRowType }) => {
            return {
              yearMonth,
              amount: sumBy(rowsByRowType['total']?.segmentColumns || [], 'amount'),
            };
          }));
          return {
            key,
            item,
            segmentRowGroups,
            totalMonthColumns,
            totalMonthColumn: totalMonthColumnOf(type, totalMonthColumns),
            hasAmount: monthRowGroups.some(({ rowGroupsByKey }) => {
              return (last(rowGroupsByKey[key]?.rows || [])?.columns || []).some(_ => Math.abs(_.amount) > 0);
            }),
          };
        }).filter(_ => showsAll || _.hasAmount);
        return {
          category,
          itemRowGroups,
        };
      }).filter(_ => _.itemRowGroups.length > 0),
    };
  });
};

const monthlyTrendsRowsForExport = (monthItems, allConsolidationSegments, showsAll) => () => {
  return generateMonthlyTrendsRowGroups(monthItems, allConsolidationSegments, showsAll).flatMap(({ rowGroups }) => {
    return rowGroups.flatMap(({ category, itemRowGroups }) => {
      const orderedItemRowGroups = sortBy(itemRowGroups, _ => _.item.itemType === 'category' ? 0 : 1);
      return orderedItemRowGroups.flatMap(({ item: { item, itemType }, segmentRowGroups }) => {
        return segmentRowGroups.flatMap(({ segment, metricRows }) => {
          return metricRows.map(({ metricLabel, companyName, monthColumns, totalMonthColumn, }) => {
            return {
              カテゴリー: category.name,
              連結科目: itemType === 'category' ? '' : item.name,
              連結セグメント: segment.id === 'general' ? '調整額' : segment.name,
              数値項目: metricLabel,
              会社: companyName || '',
              // NOTE: yearMonthをそのままキーにすると整数インデックス扱いで先頭に並ぶため、スラッシュ区切りにする
              ...fromPairs(monthColumns.map(_ => [[_.yearMonth.slice(0, 4), _.yearMonth.slice(4)].join('/'), _.amount])),
              合計: totalMonthColumn?.amount ?? '',
            };
          });
        });
      });
    });
  });
};

const rowsForExport = (...args) => () => {
  return generateRowGroups(...args).flatMap(({ type, rowGroups }) => {
    return rowGroups.flatMap(({ category, rowGroups, }) => {
      return rowGroups.flatMap(({ item: { item, itemType, }, rows, }, itemIndex) => {
        return rows.map((row) => {
          const { rowType, metricLabel, companyName, columns = [], } = row;
          return {
            category: itemType === 'category' ? item.name : '',
            consolidationAccountItemName: itemType === 'consolidationAccountItem' ? item.name : '',
            metricLabel,
            companyName,
            ...(
              columns.reduce((x, column, i) => {
                const { columnType, label, amount, } = column;
                return {
                  ...x,
                  [label]: amount,
                };
              }, {})
            ),
          };
        })
      });
    });
  });
};

exports.generateRowGroups = generateRowGroups;
exports.generateMonthlyTrendsRowGroups = generateMonthlyTrendsRowGroups;
exports.monthlyTrendsRowsForExport = monthlyTrendsRowsForExport;
exports.rowTypeLabels = rowTypeLabels;
exports.rowsForExport = rowsForExport;
